import { useState, useCallback, useRef, useMemo } from 'react';

export interface DragAndDropState {
  isDragging: boolean;
  draggedItemId: string | null;
  dragOverItemId: string | null;
}

export interface UseDragAndDropOptions {
  onReorder?: (fromIndex: number, toIndex: number) => void;
  liveReorder?: boolean; // Nueva opción para intercambio en vivo
  throttleMs?: number; // Tiempo de throttle para dragOver (default: 50ms)
}

export interface DragHandlers {
  onDragStart: (e: React.DragEvent, itemId: string, index: number) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent, itemId: string, index: number) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, itemId: string, index: number) => void;
}

/**
 * Hook personalizado para manejar drag and drop con optimización de rendimiento
 * Proporciona handlers y estado para implementar reordenamiento de items
 */
export const useDragAndDrop = (options?: UseDragAndDropOptions) => {
  const { onReorder, liveReorder = false, throttleMs = 50 } = options || {};

  const [state, setState] = useState<DragAndDropState>({
    isDragging: false,
    draggedItemId: null,
    dragOverItemId: null,
  });

  // Refs para mantener los índices durante el drag
  const draggedIndexRef = useRef<number>(-1);
  const dragOverIndexRef = useRef<number>(-1);
  const lastSwapIndexRef = useRef<number>(-1); // Para evitar swaps repetitivos
  const lastSwapTimeRef = useRef<number>(0); // Para throttling manual
  const animationFrameRef = useRef<number | null>(null); // Para requestAnimationFrame

  const handleDragStart = useCallback((e: React.DragEvent, itemId: string, index: number) => {
    draggedIndexRef.current = index;
    lastSwapIndexRef.current = index; // Inicializar con el índice actual

    setState((prev) => ({
      ...prev,
      isDragging: true,
      draggedItemId: itemId,
    }));

    // Configurar el efecto visual del drag
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', itemId);

    // Obtener el elemento completo del item (el padre del drag handle)
    const dragHandle = e.currentTarget as HTMLElement;
    const listItem = dragHandle.parentElement as HTMLElement;

    if (listItem) {
      // Crear canvas con la imagen del elemento
      const rect = listItem.getBoundingClientRect();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Configurar tamaño del canvas
      const scale = window.devicePixelRatio || 1;
      canvas.width = rect.width * scale;
      canvas.height = rect.height * scale;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      if (ctx) {
        ctx.scale(scale, scale);

        // Obtener colores del sistema de diseño desde el elemento
        const computedStyle = window.getComputedStyle(listItem);
        const hoverBg =
          getComputedStyle(document.documentElement)
            .getPropertyValue('--color-bg-fill-hover')
            .trim() || '#f0f1f2';
        const borderColor =
          getComputedStyle(document.documentElement)
            .getPropertyValue('--color-border-default')
            .trim() || '#c6c8cc';
        const textPrimary =
          getComputedStyle(document.documentElement)
            .getPropertyValue('--color-text-primary')
            .trim() || '#313336';
        const iconDefault =
          getComputedStyle(document.documentElement)
            .getPropertyValue('--color-icon-default')
            .trim() || '#949494';

        // Fondo hover con bordes redondeados
        ctx.fillStyle = hoverBg;
        ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 2;

        const borderRadius = 8;
        ctx.beginPath();
        ctx.moveTo(borderRadius, 0);
        ctx.lineTo(rect.width - borderRadius, 0);
        ctx.quadraticCurveTo(rect.width, 0, rect.width, borderRadius);
        ctx.lineTo(rect.width, rect.height - borderRadius);
        ctx.quadraticCurveTo(rect.width, rect.height, rect.width - borderRadius, rect.height);
        ctx.lineTo(borderRadius, rect.height);
        ctx.quadraticCurveTo(0, rect.height, 0, rect.height - borderRadius);
        ctx.lineTo(0, borderRadius);
        ctx.quadraticCurveTo(0, 0, borderRadius, 0);
        ctx.closePath();
        ctx.fill();

        // Borde semántico
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = 1;
        ctx.stroke();

        // Agregar texto del elemento
        ctx.fillStyle = textPrimary;
        ctx.font = '500 14px Inter, system-ui, sans-serif';
        ctx.textBaseline = 'middle';

        // Obtener el texto del elemento - buscar en múltiples selectores posibles
        let textElement = listItem.querySelector('[class*="font-semibold"]');
        if (!textElement) {
          // Buscar en otros selectores comunes de texto (para ListItemToggle y otros)
          textElement =
            listItem.querySelector('[class*="ui-font-body-sm-medium"]') ||
            listItem.querySelector('[class*="ui-text-body-sm"]') ||
            listItem.querySelector('[class*="ui-text-primary"]') ||
            listItem.querySelector('[class*="font-body-sm-medium"]') ||
            listItem.querySelector('[class*="text-primary"]') ||
            listItem.querySelector('.ui-flex-1 > div:first-child');
        }
        if (textElement) {
          const text = textElement.textContent || '';
          ctx.fillText(text, 40, rect.height / 2);
        }

        // Dibujar icono de drag (grip dots)
        ctx.fillStyle = iconDefault;
        const dotSize = 2;
        const spacing = 4;
        const startX = 12;
        const centerY = rect.height / 2;

        for (let row = 0; row < 3; row++) {
          for (let col = 0; col < 2; col++) {
            const x = startX + col * spacing;
            const y = centerY - spacing + row * spacing;
            ctx.beginPath();
            ctx.arc(x, y, dotSize, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      // Establecer el canvas como imagen de drag
      canvas.style.position = 'absolute';
      canvas.style.top = '-9999px';
      canvas.style.left = '-9999px';
      document.body.appendChild(canvas);

      // Offset para que el cursor esté sobre el grip handle (lado izquierdo)
      // X: ~20px desde el borde izquierdo (donde está el grip)
      // Y: Centro vertical del elemento
      e.dataTransfer.setDragImage(canvas, 20, rect.height / 2);

      // Limpiar después
      setTimeout(() => {
        if (document.body.contains(canvas)) {
          document.body.removeChild(canvas);
        }
      }, 0);
    }
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    // Cancelar cualquier animationFrame pendiente
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    setState({
      isDragging: false,
      draggedItemId: null,
      dragOverItemId: null,
    });

    draggedIndexRef.current = -1;
    dragOverIndexRef.current = -1;
    lastSwapIndexRef.current = -1;
    lastSwapTimeRef.current = 0;
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, itemId: string, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      dragOverIndexRef.current = index;

      // Si liveReorder está activado, hacer el intercambio con throttling
      if (liveReorder && draggedIndexRef.current !== -1 && draggedIndexRef.current !== index) {
        const now = Date.now();

        // Throttling: Solo ejecutar si ha pasado suficiente tiempo desde el último swap
        if (now - lastSwapTimeRef.current >= throttleMs && lastSwapIndexRef.current !== index) {
          // Cancelar cualquier animationFrame pendiente
          if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
          }

          // Usar requestAnimationFrame para sincronizar con el render del navegador
          animationFrameRef.current = requestAnimationFrame(() => {
            onReorder?.(draggedIndexRef.current, index);
            draggedIndexRef.current = index;
            lastSwapIndexRef.current = index;
            lastSwapTimeRef.current = Date.now();
            animationFrameRef.current = null;
          });
        }
      }

      // Optimizar actualización de estado - solo actualizar si cambió
      setState((prev) => {
        if (prev.dragOverItemId !== itemId) {
          return {
            ...prev,
            dragOverItemId: itemId,
          };
        }
        return prev;
      });
    },
    [liveReorder, onReorder, throttleMs],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, _itemId: string, index: number) => {
      e.preventDefault();
      e.stopPropagation();

      // Cancelar cualquier animationFrame pendiente
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Si liveReorder está activado, el reordenamiento ya se hizo en dragOver
      // Solo limpiamos el estado
      if (!liveReorder) {
        const fromIndex = draggedIndexRef.current;
        const toIndex = index;

        if (fromIndex !== -1 && fromIndex !== toIndex) {
          onReorder?.(fromIndex, toIndex);
        }
      }

      setState({
        isDragging: false,
        draggedItemId: null,
        dragOverItemId: null,
      });

      draggedIndexRef.current = -1;
      dragOverIndexRef.current = -1;
      lastSwapIndexRef.current = -1;
      lastSwapTimeRef.current = 0;
    },
    [liveReorder, onReorder],
  );

  // Memoizar handlers para evitar re-renders innecesarios
  const handlers: DragHandlers = useMemo(
    () => ({
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    }),
    [handleDragStart, handleDragEnd, handleDragOver, handleDragLeave, handleDrop],
  );

  return {
    state,
    handlers,
    isDragging: state.isDragging,
    draggedItemId: state.draggedItemId,
    dragOverItemId: state.dragOverItemId,
  };
};
